import { useEffect, useMemo, useState } from 'react';
import type { SuggestedTestCase } from '../types';
import { useAuth } from '../auth';
import { evaluateGithubAlignment, type GithubAlignmentResponse } from '../api';
import CodeViewerModal from './CodeViewerModal';

export type GithubContextForReport = {
  repo_full_name: string;
  commits: { sha: string; message: string; author?: string | null; date?: string | null; html_url?: string | null }[];
  merged: {
    focus_areas: { title: string; severity: 'high' | 'medium' | 'low'; reason: string; related_files: string[] }[];
    side_effects: string[];
    suggested_focus_areas: string[];
  };
  highlights?: {
    important_symbols: {
      symbol: string;
      kind: string;
      path: string;
      reason: string;
      snippet?: { path: string; start_line: number; end_line: number; content: string } | null;
    }[];
    file_patches: { filename: string; status?: string | null; additions?: number | null; deletions?: number | null; patch?: string | null }[];
  } | null;
  captured_at?: string;
};

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9가-힣\s]/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 2);
}

function computeAlignmentScore(needles: string[], haystack: string): { score: number; matched: string[]; unmatched: string[] } {
  const tokens = new Set(tokenize(haystack));
  const matched: string[] = [];
  const unmatched: string[] = [];
  for (const n of needles) {
    const nt = tokenize(n);
    const ok = nt.some((t) => tokens.has(t));
    (ok ? matched : unmatched).push(n);
  }
  const score = needles.length ? Math.round((matched.length / needles.length) * 100) : 0;
  return { score, matched, unmatched };
}

export default function GitHubReportBlock(props: {
  lang: 'ko' | 'en';
  githubCtx: GithubContextForReport | null;
  inputData: {
    planning_intent: string[];
    risks: string[];
    suggested_testcases?: SuggestedTestCase[] | null;
  };
}) {
  const { user } = useAuth();
  const { lang, githubCtx, inputData } = props;

  const [githubAi, setGithubAi] = useState<GithubAlignmentResponse | null>(null);
  const [githubAiLoading, setGithubAiLoading] = useState(false);
  const [githubAiError, setGithubAiError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<{
    isOpen: boolean;
    title: string;
    subtitle?: string | null;
    mode: 'text' | 'diff';
    content: string;
  }>({ isOpen: false, title: '', subtitle: null, mode: 'text', content: '' });

  const derived = useMemo(() => {
    if (!githubCtx) return null;
    const planningText = (inputData.planning_intent || []).join('\n');
    const riskText = (inputData.risks || []).join('\n');
    const testcaseText = (inputData.suggested_testcases || []).map((tc) => `${tc.title}\n${tc.description}`).join('\n');
    const focusTitles = githubCtx.merged.focus_areas.map((f) => f.title);
    const sideEffects = githubCtx.merged.side_effects;
    const toCheck = [...focusTitles, ...sideEffects];
    const alignPlanning = computeAlignmentScore(toCheck, `${planningText}\n${riskText}`);
    const alignTestcases = computeAlignmentScore(toCheck, testcaseText);
    return { alignPlanning, alignTestcases, sideEffects };
  }, [githubCtx, inputData.planning_intent, inputData.risks, inputData.suggested_testcases]);

  // AI 근거 기반 부합도 평가 호출 (로그인 + githubCtx 있을 때)
  useEffect(() => {
    const run = async () => {
      if (!user || !githubCtx) {
        setGithubAi(null);
        setGithubAiError(null);
        return;
      }
      setGithubAiLoading(true);
      setGithubAiError(null);
      try {
        const res = await evaluateGithubAlignment({
          github: {
            repo_full_name: githubCtx.repo_full_name,
            commits: githubCtx.commits,
            merged: githubCtx.merged,
          },
          planning_intent: inputData.planning_intent,
          risks: inputData.risks,
          suggested_testcases: inputData.suggested_testcases ?? null,
          lang,
        });
        setGithubAi(res);
      } catch (e) {
        setGithubAi(null);
        setGithubAiError(e instanceof Error ? e.message : (lang === 'ko' ? '부합도 평가에 실패했습니다' : 'Failed to evaluate alignment'));
      } finally {
        setGithubAiLoading(false);
      }
    };
    run();
  }, [user, githubCtx, inputData.planning_intent, inputData.risks, inputData.suggested_testcases, lang]);

  if (!githubCtx?.commits?.length || !derived) return null;

  const { alignPlanning, alignTestcases, sideEffects } = derived;

  return (
    <div className="border border-terminal-border rounded-lg p-4 bg-terminal-dim/5">
      <CodeViewerModal
        isOpen={viewer.isOpen}
        onClose={() => setViewer((v) => ({ ...v, isOpen: false }))}
        title={viewer.title}
        subtitle={viewer.subtitle}
        content={viewer.content}
        mode={viewer.mode}
      />

      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-terminal-dim text-xs mb-1">
            {lang === 'ko' ? 'GitHub 커밋 분석(선택)' : 'GitHub Commit Analysis (Selected)'}
          </div>
          <div className="text-sm text-terminal-text font-semibold">{githubCtx.repo_full_name}</div>
          <div className="text-xs text-terminal-muted mt-1">
            {lang === 'ko' ? `${githubCtx.commits.length}개 커밋 기준` : `Based on ${githubCtx.commits.length} commits`}
          </div>
        </div>
        <div className="text-right text-xs">
          <div className="text-terminal-muted">{lang === 'ko' ? '부합도(기획/리스크)' : 'Alignment (Plan/Risk)'}</div>
          <div className="text-terminal-text font-bold">
            {githubAi?.planning_risk_alignment?.score ?? alignPlanning.score}%
          </div>
          <div className="text-terminal-muted mt-1">{lang === 'ko' ? '부합도(테스트케이스)' : 'Alignment (Testcases)'}</div>
          <div className="text-terminal-text font-bold">
            {githubAi?.testcase_alignment?.score ?? alignTestcases.score}%
          </div>
        </div>
      </div>

      {/* 커밋 목록 */}
      <div className="mt-3 flex flex-wrap gap-2">
        {githubCtx.commits.slice(0, 10).map((c) => (
          <span key={c.sha} className="text-[11px] px-2 py-1 border border-terminal-border rounded bg-terminal-dim/10 text-terminal-text font-mono">
            {c.sha.slice(0, 7)}
          </span>
        ))}
        {githubCtx.commits.length > 10 && (
          <span className="text-[11px] text-terminal-muted">
            {lang === 'ko' ? `외 ${githubCtx.commits.length - 10}개` : `+${githubCtx.commits.length - 10} more`}
          </span>
        )}
      </div>

      {/* 주의 사이드이펙트 */}
      <div className="mt-4">
        <div className="text-terminal-dim text-xs mb-2">
          {lang === 'ko' ? '주의해야 할 사이드이펙트' : 'Potential Side Effects to Watch'}
        </div>
        {sideEffects.length ? (
          <ul className="space-y-1 text-sm text-terminal-text">
            {sideEffects.slice(0, 8).map((s) => (
              <li key={s} className="flex items-start gap-2">
                <span className="text-status-warning">⚠</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-terminal-muted">{lang === 'ko' ? '감지된 항목이 없습니다.' : 'No items detected.'}</div>
        )}
        {sideEffects.length > 8 && (
          <div className="text-xs text-terminal-muted mt-2">
            {lang === 'ko' ? `외 ${sideEffects.length - 8}개` : `+${sideEffects.length - 8} more`}
          </div>
        )}
      </div>

      {/* 주요 코드(스니펫/patch) */}
      {(githubCtx.highlights?.important_symbols?.length || githubCtx.highlights?.file_patches?.length) ? (
        <div className="mt-4 border border-terminal-border rounded-lg p-3 bg-terminal-dim/10">
          <div className="text-terminal-dim text-xs mb-2">{lang === 'ko' ? '주요 코드(스니펫/patch)' : 'Key code (snippets/patch)'}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-terminal-border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-terminal-dim/10 text-xs text-terminal-muted">
                {lang === 'ko' ? '중요 함수/클래스(스니펫)' : 'Important symbols (snippets)'} · {(githubCtx.highlights?.important_symbols?.length ?? 0)}
              </div>
              <div className="p-3 space-y-2">
                {(githubCtx.highlights?.important_symbols ?? []).slice(0, 6).map((s) => {
                  const lineInfo = s.snippet?.start_line && s.snippet?.end_line ? `${s.snippet.start_line}-${s.snippet.end_line}` : '';
                  const subtitle = `${s.path}${lineInfo ? `:${lineInfo}` : ''}`;
                  return (
                    <div key={`${s.path}::${s.kind}::${s.symbol}`} className="border border-terminal-border rounded-lg overflow-hidden">
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 bg-terminal-dim/10 hover:bg-terminal-dim/15 transition-colors flex items-center justify-between gap-3"
                        onClick={() =>
                          setViewer({
                            isOpen: true,
                            title: `${s.kind} ${s.symbol}`,
                            subtitle,
                            mode: 'text',
                            content: s.snippet?.content || (lang === 'ko' ? '스니펫을 생성하지 못했습니다.' : 'No snippet available.'),
                          })
                        }
                      >
                        <div className="min-w-0">
                          <div className="text-sm text-terminal-text font-semibold truncate">
                            <span className="font-mono text-xs mr-2">{s.kind}</span>
                            {s.symbol}
                          </div>
                          <div className="text-xs text-terminal-muted font-mono truncate">{subtitle}</div>
                        </div>
                        <div className="text-xs text-terminal-muted whitespace-nowrap min-w-[44px] text-right">{lang === 'ko' ? '보기' : 'View'}</div>
                      </button>
                    </div>
                  );
                })}
                {(githubCtx.highlights?.important_symbols?.length ?? 0) === 0 && (
                  <div className="text-xs text-terminal-muted">{lang === 'ko' ? '표시할 스니펫이 없습니다.' : 'No snippets to show.'}</div>
                )}
                {(githubCtx.highlights?.important_symbols?.length ?? 0) > 6 && (
                  <div className="text-[11px] text-terminal-muted">
                    {lang === 'ko'
                      ? `외 ${(githubCtx.highlights?.important_symbols?.length ?? 0) - 6}개`
                      : `+${(githubCtx.highlights?.important_symbols?.length ?? 0) - 6} more`}
                  </div>
                )}
              </div>
            </div>

            <div className="border border-terminal-border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-terminal-dim/10 text-xs text-terminal-muted">
                {lang === 'ko' ? '변경 파일 patch(요약)' : 'Changed file patches (summary)'} · {(githubCtx.highlights?.file_patches?.length ?? 0)}
              </div>
              <div className="p-3 space-y-2">
                {(githubCtx.highlights?.file_patches ?? []).slice(0, 6).map((f) => {
                  const meta = `${f.status || 'unknown'} · +${f.additions ?? 0}/-${f.deletions ?? 0}`;
                  return (
                    <div key={f.filename} className="border border-terminal-border rounded-lg overflow-hidden">
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 bg-terminal-dim/10 hover:bg-terminal-dim/15 transition-colors flex items-center justify-between gap-3"
                        onClick={() =>
                          setViewer({
                            isOpen: true,
                            title: f.filename,
                            subtitle: meta,
                            mode: 'diff',
                            content: f.patch || (lang === 'ko' ? 'patch가 제공되지 않았습니다.' : 'No patch provided.'),
                          })
                        }
                      >
                        <div className="min-w-0">
                          <div className="text-sm text-terminal-text font-semibold truncate font-mono">{f.filename}</div>
                          <div className="text-xs text-terminal-muted">{meta}</div>
                        </div>
                        <div className="text-xs text-terminal-muted whitespace-nowrap min-w-[44px] text-right">{lang === 'ko' ? '보기' : 'View'}</div>
                      </button>
                    </div>
                  );
                })}
                {(githubCtx.highlights?.file_patches?.length ?? 0) === 0 && (
                  <div className="text-xs text-terminal-muted">{lang === 'ko' ? '표시할 patch가 없습니다.' : 'No patches to show.'}</div>
                )}
                {(githubCtx.highlights?.file_patches?.length ?? 0) > 6 && (
                  <div className="text-[11px] text-terminal-muted">
                    {lang === 'ko'
                      ? `외 ${(githubCtx.highlights?.file_patches?.length ?? 0) - 6}개`
                      : `+${(githubCtx.highlights?.file_patches?.length ?? 0) - 6} more`}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* 미부합(커버되지 않은 항목) */}
      {((githubAi?.testcase_alignment?.gaps?.length ?? 0) > 0 || alignTestcases.unmatched.length > 0) && (
        <div className="mt-4 p-3 bg-status-danger/10 border border-status-danger/30 rounded">
          <div className="text-status-danger text-xs font-semibold mb-2">
            {lang === 'ko' ? '테스트케이스에서 아직 커버되지 않은 항목(추정)' : 'Uncovered items in testcases (estimated)'}
          </div>
          <ul className="space-y-1 text-xs text-terminal-text">
            {(githubAi?.testcase_alignment?.gaps?.length ? githubAi.testcase_alignment.gaps.map((g) => g.item) : alignTestcases.unmatched)
              .slice(0, 8)
              .map((u) => (
                <li key={u} className="flex items-start gap-2">
                  <span className="text-terminal-dim">•</span>
                  <span>{u}</span>
                </li>
              ))}
          </ul>
          {((githubAi?.testcase_alignment?.gaps?.length ?? 0) > 8 || alignTestcases.unmatched.length > 8) && (
            <div className="text-xs text-terminal-muted mt-2">
              {lang === 'ko'
                ? `외 ${(githubAi?.testcase_alignment?.gaps?.length ?? alignTestcases.unmatched.length) - 8}개`
                : `+${(githubAi?.testcase_alignment?.gaps?.length ?? alignTestcases.unmatched.length) - 8} more`}
            </div>
          )}
        </div>
      )}

      {/* AI 근거 출력 */}
      {user && (
        <div className="mt-4">
          <div className="text-terminal-dim text-xs mb-2">{lang === 'ko' ? 'AI 부합/미부합 근거' : 'AI Evidence (Alignment)'}</div>
          {githubAiLoading ? (
            <div className="text-xs text-terminal-muted">{lang === 'ko' ? 'AI가 근거를 작성 중입니다...' : 'AI is generating evidence...'}</div>
          ) : githubAiError ? (
            <div className="text-xs text-status-danger">{githubAiError}</div>
          ) : githubAi ? (
            <div className="space-y-3">
              <div className="p-3 border border-terminal-border rounded-lg bg-terminal-dim/10">
                <div className="text-xs text-terminal-muted mb-2">{lang === 'ko' ? '기획/리스크 부합(근거 포함)' : 'Plan/Risk Alignment (with evidence)'}</div>
                {githubAi.planning_risk_alignment.aligned?.length ? (
                  <ul className="space-y-2 text-xs text-terminal-text">
                    {githubAi.planning_risk_alignment.aligned.slice(0, 4).map((it) => (
                      <li key={it.item}>
                        <div className="font-semibold">✅ {it.item}</div>
                        <div className="text-terminal-muted mt-0.5">{it.reason}</div>
                        {it.evidence?.length ? (
                          <ul className="mt-1 space-y-1">
                            {it.evidence.slice(0, 2).map((ev, idx) => (
                              <li key={idx} className="text-terminal-dim">
                                - [{ev.source}] “{ev.quote}”
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-terminal-muted">
                    {lang === 'ko'
                      ? '표시할 부합 근거가 없습니다. (AI가 부합 항목을 생성하지 못했거나 입력이 부족할 수 있어요)'
                      : 'No alignment evidence to show. (AI may not have produced aligned items, or inputs may be insufficient.)'}
                  </div>
                )}
              </div>

              <div className="p-3 border border-terminal-border rounded-lg bg-terminal-dim/10">
                <div className="text-xs text-terminal-muted mb-2">{lang === 'ko' ? '테스트케이스 부합(근거 포함)' : 'Testcase Alignment (with evidence)'}</div>
                {githubAi.testcase_alignment.aligned?.length ? (
                  <ul className="space-y-2 text-xs text-terminal-text">
                    {githubAi.testcase_alignment.aligned.slice(0, 4).map((it) => (
                      <li key={it.item}>
                        <div className="font-semibold">✅ {it.item}</div>
                        <div className="text-terminal-muted mt-0.5">{it.reason}</div>
                        {it.evidence?.length ? (
                          <ul className="mt-1 space-y-1">
                            {it.evidence.slice(0, 2).map((ev, idx) => (
                              <li key={idx} className="text-terminal-dim">
                                - [{ev.source}] “{ev.quote}”
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-terminal-muted">
                    {lang === 'ko'
                      ? '표시할 부합 근거가 없습니다. (테스트케이스가 비어있거나 AI 생성이 실패했을 수 있어요)'
                      : 'No testcase alignment evidence to show. (Testcases may be empty, or AI generation may have failed.)'}
                  </div>
                )}
              </div>

              {githubAi.summary?.overall_comment && <div className="text-xs text-terminal-muted">{githubAi.summary.overall_comment}</div>}
            </div>
          ) : (
            <div className="text-xs text-terminal-muted">{lang === 'ko' ? 'AI 근거를 표시하려면 로그인이 필요합니다.' : 'Login required to show AI evidence.'}</div>
          )}
        </div>
      )}
    </div>
  );
}


